/**
 * PATH: apps/quote/src/components/builder/scope-sheet-grid/ScopeSheetGridTypes.ts
 * PURPOSE: Types and interfaces for the ScopeSheetGrid component family.
 */

import type { QuoteSection, QuoteLineItem } from '@/lib/quoteApi'
import type { CellErrorState } from '@/hooks/useBuilderEditsBuffer'

export type GridRowType =
  | { type: 'section-header'; section: QuoteSection }
  | { type: 'line-item'; section: QuoteSection; item: QuoteLineItem; index: number; flatRowIndex: number }
  | { type: 'phantom'; section: QuoteSection }

export interface ScopeSheetGridProps {
  sections: QuoteSection[]
  isEditable: boolean
  onAddItem: (sectionId: string, data: {
    location?: string
    description: string
    quantity: number
    unit: string
    productivity_pmpd: number
    labour_chargeout?: number | null
    material_chargeout?: number | null
    plant_chargeout?: number | null
    subcon_chargeout?: number | null
  }) => void
  onUpdateItem: (sectionId: string, itemId: string, patch: Partial<QuoteLineItem>) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
  onAddSection: (title: string) => void
  onRenameSection: (sectionId: string, newTitle: string) => void
  onDeleteSection: (sectionId: string) => void
  onSelectItem?: (sectionId: string, itemId: string) => void
  selectedItemId?: string | null
  isAddingSectionPending?: boolean
  focusedSectionId?: string | null
  onFocusSection?: (sectionId: string | null) => void
  onPasteWithCreate?: (
    sectionId: string,
    existingItems: QuoteLineItem[],
    pasteData: Array<Record<string, unknown>>,
    startRow: number
  ) => Promise<void>
  getOptimisticValue?: (itemId: string, field: string) => unknown | undefined
  getCellErrorState?: (itemId: string, field: string) => CellErrorState
  isSaving?: boolean
  requestedPhantomFocusSectionId?: string | null
  onRequestedPhantomFocusHandled?: () => void
  requestedScrollToSectionId?: string | null
  onRequestedScrollToSectionHandled?: () => void
  externalPaste?: { nonce: number; data: string[][] } | null
  onExternalPasteHandled?: () => void
  /**
   * Quote-level config defaults from the Setup tab. Forwarded into PhantomRow
   * so new line items inherit the operator's per-quote pmpd / unit settings
   * instead of the hardcoded fallback (pmpd=15, unit=m2).
   */
  configDefaults?: {
    defaultProductivityPmpd?: number | string | null
    defaultUnit?: string
  }
  /**
   * User-defined custom columns (declared in `quote.config.custom_columns`).
   * When non-empty, ScopeSheetGrid appends a "Custom" band; per-line values
   * live in `QuoteLineItem.custom_values[key]`. Cascade resolution at display
   * time: line.custom_values[key] > customColumns[i].default_value.
   */
  customColumns?: CustomColumnDef[]
  /**
   * Quote-level opt-in for advanced columns (declared in
   * `quote.config.visible_columns`). Each entry is the id of an `optional`
   * column declared in BASE_COLUMN_BANDS. Columns NOT listed stay hidden
   * (track width = 0). Empty/undefined → only the mandatory baseline is
   * visible — Setup → "Visible columns" turns them on.
   */
  visibleColumnIds?: string[]
  /**
   * Persist a partial patch onto `quote.config` (used by inline column
   * header menu to toggle `visible_columns` without a Setup round-trip).
   * Implementation must merge into existing config and PATCH the quote.
   */
  onUpdateConfig?: (patch: Record<string, unknown>) => void
  /**
   * Duplicate the given line item — must clone all serialised fields,
   * strip server-owned ids/timestamps, and POST a fresh row immediately
   * after the source. Right-click row menu uses this for "Duplicate row".
   */
  onDuplicateItem?: (sectionId: string, itemId: string) => void
  /**
   * Insert a fresh empty row above/below the given anchor row. Right-click
   * row menu uses these to build out a quote without leaving the keyboard.
   * If unimplemented, the inserted row falls back to the section tail.
   */
  onInsertRowAbove?: (sectionId: string, itemId: string) => void
  onInsertRowBelow?: (sectionId: string, itemId: string) => void
}

export interface ColumnBand {
  id: string
  label: string
  color: string
  bgClass: string
  textClass: string
  columns: Column[]
  /**
   * Optional nested band tree. When present, AG-Grid renders this band
   * as a parent header with `subBands` as inner column groups (e.g.
   * "PRODUCTS" → ["Primer", "Basecoat", "Topcoat", "Accessories"] →
   * actual columns). `columns` is concatenated after `subBands` so
   * aggregate columns can sit alongside slot sub-groups in the same
   * parent band (see QB-AGGRID-BRAVADA-PRICING-001 for the Bravada
   * PRODUCTS band layout). Single-level bands omit this field.
   */
  subBands?: ColumnBand[]
}

export interface Column {
  id: string
  label: string
  width: number | 'flex'
  align?: 'left' | 'center' | 'right'
  computed?: boolean
  editable?: boolean
  override?: boolean
  hidden?: boolean
  /**
   * Optional columns are not visible by default. They show up only when the
   * column id is present in `quote.config.visible_columns` (or when the user
   * toggles them on via the Setup → "Visible columns" picker). Distinct from
   * `hidden`: `hidden` is a hard "do not render" set by the view preset;
   * `optional` is a soft "off by default, opt-in".
   */
  optional?: boolean
  /**
   * Custom columns are user-defined fields stored in `Quote.config.custom_columns`.
   * Per-line values live in `QuoteLineItem.custom_values[key]`. The cascade is:
   *   line.custom_values[key]  >  column.defaultValue
   * When `custom: true`, ScopeSheetGrid + LineItemRow route reads/writes through
   * `custom_values` instead of treating `id` as a real model field name.
   */
  custom?: boolean
  /** Display + edit type for custom columns. */
  type?: 'text' | 'number' | 'currency'
  /** Setup-level fallback when this line has no override. */
  defaultValue?: string | number | null
}

/**
 * User-defined column declaration persisted on Quote.config.custom_columns.
 * Keys are stable per-quote identifiers. Renames create a new entry; old keys
 * are gracefully tolerated (read returns undefined, falls through to default).
 */
export interface CustomColumnDef {
  key: string
  label: string
  type?: 'text' | 'number' | 'currency'
  width?: number
  default_value?: string | number | null
  sort_order?: number
}

export const ROW_HEIGHTS: Record<string, number> = {
  'section-header': 48,
  'line-item': 36,
  'phantom': 36,
}
